import React, { useMemo } from "react";
import { Box, Paper, Typography, CircularProgress, Grid } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from "recharts";
import { yearChartColor } from "./pygChartCompareYears";
import {
  buildYearCompareChartData,
  yearDataKey,
} from "./pygYearCompareChartData";
import { ARAMIS_DATA_KEY, ARAMIS_LABEL } from "./pygAramis";
import { useTranslation } from "react-i18next";

const yearsJoin = (years, sep) => years.join(sep);

const variantCopy = (t, variant, years) => {
  const yVs = yearsJoin(years, " vs ");
  const yAnd = yearsJoin(years, " · ");
  if (variant === "venta") {
    return {
      rowLabel: t("pygAnalitico.charts.ventaRow"),
      monthlyTitle: t("pygAnalitico.charts.ventaMonthly", { years: yVs }),
      acumTitle: t("pygAnalitico.charts.ventaAcum", { years: yVs }),
      loadingText: t("pygAnalitico.charts.ventaLoading", { years: yAnd }),
      errorPrefix: t("pygAnalitico.charts.ventaError"),
    };
  }
  return {
    rowLabel: t("pygAnalitico.charts.resultadoRow"),
    monthlyTitle: t("pygAnalitico.charts.resultadoMonthly", { years: yVs }),
    acumTitle: t("pygAnalitico.charts.resultadoAcum", { years: yVs }),
    loadingText: t("pygAnalitico.charts.resultadoLoading", { years: yAnd }),
    errorPrefix: t("pygAnalitico.charts.resultadoError"),
  };
};

const fmtAxis = (v) => {
  if (typeof v !== "number" || Number.isNaN(v)) return v;
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${Math.round(v / 1_000)}k`;
  return String(Math.round(v));
};

const chartPaperSx = (theme) => ({
  p: { xs: 1.25, sm: 1.5 },
  borderRadius: 2.5,
  border: "1px solid",
  borderColor: alpha(theme.palette.divider, 0.85),
  backgroundColor: theme.palette.background.paper,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 6px 18px rgba(0,0,0,0.28)"
      : "0 6px 16px rgba(15,23,42,0.06)",
  height: "100%",
});

const ChartTooltip = ({
  active,
  payload,
  label,
  formatearImporte,
  aramisAsOfMonth,
  t,
}) => {
  if (!active || !payload?.length) return null;
  const mesNum = Number(String(label).replace(/^M/, "")) || null;
  const aramisSuffix =
    aramisAsOfMonth && mesNum
      ? mesNum < aramisAsOfMonth
        ? ` · ${t("pygAnalitico.charts.aramisBacktest")}`
        : mesNum >= aramisAsOfMonth
          ? ` · ${t("pygAnalitico.charts.aramisProjection")}`
          : ""
      : "";
  return (
    <Paper sx={{ p: 1.25 }} elevation={4}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 800, display: "block", mb: 0.5 }}
      >
        {label}
        {aramisSuffix}
      </Typography>
      {payload.map((entry) => (
        <Typography
          key={entry.dataKey || entry.name}
          variant="caption"
          display="block"
          sx={{ color: entry.color }}
        >
          {entry.name}: {formatearImporte(entry.value ?? 0)}
        </Typography>
      ))}
    </Paper>
  );
};

const PygAnaliticoYearCompareChart = ({
  variant = "resultado",
  compareYears = [],
  serieByYear,
  aramisSerieByYear,
  loading,
  error,
  formatearImporte,
  vistaLabel,
  marcaLabel,
  enableAramis = true,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const years = useMemo(
    () => [...compareYears].sort((a, b) => a - b),
    [compareYears],
  );
  const cfg = variantCopy(t, variant, years);

  const { chartData, acumChartData, aramis } = useMemo(
    () =>
      buildYearCompareChartData(serieByYear, years, {
        enableAramis,
        aramisSerieByYear,
      }),
    [serieByYear, aramisSerieByYear, years, enableAramis],
  );

  const aramisColor = alpha(theme.palette.text.primary, 0.42);
  const gridStroke = alpha(theme.palette.divider, 0.9);

  if (loading) {
    return (
      <Box
        sx={{
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <CircularProgress size={22} />
        <Typography variant="body2" color="text.secondary">
          {cfg.loadingText}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" variant="body2" sx={{ py: 1 }}>
        {cfg.errorPrefix}: {error}
      </Typography>
    );
  }

  if (!chartData.length || !years.length) return null;

  const totales = years.map((year) => ({
    year,
    total: serieByYear?.[year]?.total ?? 0,
  }));

  const aramisYearEnd =
    aramis && acumChartData.length
      ? acumChartData[acumChartData.length - 1]?.[ARAMIS_DATA_KEY]
      : null;

  const filterText = `${vistaLabel || ""}${marcaLabel ? ` · ${marcaLabel}` : ""}`;
  const totalsText = totales
    .map(({ year, total }) => `${year}: ${formatearImporte(total)}`)
    .join(" · ");

  const renderLineChart = (data, height = 260) => (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
        <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
        <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 11 }} width={52} />
        <Tooltip
          content={
            <ChartTooltip
              formatearImporte={formatearImporte}
              aramisAsOfMonth={aramis?.asOfMonth}
              t={t}
            />
          }
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {variant === "resultado" && (
          <ReferenceLine y={0} stroke={theme.palette.text.disabled} />
        )}
        {years.map((year, idx) => (
          <Line
            key={year}
            type="linear"
            dataKey={yearDataKey(year)}
            name={String(year)}
            stroke={yearChartColor(theme, idx)}
            strokeWidth={2.5}
            connectNulls
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
        ))}
        {aramis && (
          <Line
            type="linear"
            dataKey={ARAMIS_DATA_KEY}
            name={ARAMIS_LABEL}
            stroke={aramisColor}
            strokeWidth={2}
            strokeDasharray="7 5"
            connectNulls
            dot={{ r: 2.5, strokeDasharray: "" }}
            activeDot={{ r: 5 }}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 1 }}
      >
        {t("pygAnalitico.charts.totalsLine", {
          filter: filterText,
          row: cfg.rowLabel,
          totals: totalsText,
        })}
        {aramisYearEnd != null
          ? ` · ${t("pygAnalitico.charts.aramisClose", {
              label: ARAMIS_LABEL,
              value: formatearImporte(aramisYearEnd),
            })}`
          : ""}
      </Typography>
      {aramis && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 1 }}
        >
          {t("pygAnalitico.charts.aramisHint", {
            label: ARAMIS_LABEL,
            month: aramis.asOfMonth,
          })}
        </Typography>
      )}
      <Grid container spacing={1.5}>
        <Grid item xs={12} lg={6}>
          <Paper sx={chartPaperSx(theme)}>
            <Typography variant="subtitle2" fontWeight={800} gutterBottom>
              {cfg.monthlyTitle}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mb: 1 }}
            >
              {t("pygAnalitico.charts.monthlyNotAcum")}
            </Typography>
            {renderLineChart(chartData)}
          </Paper>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Paper sx={chartPaperSx(theme)}>
            <Typography variant="subtitle2" fontWeight={800} gutterBottom>
              {cfg.acumTitle}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mb: 1 }}
            >
              {t("pygAnalitico.charts.acumHint")}
              {aramis
                ? ` · ${t("pygAnalitico.charts.acumAramis", { label: ARAMIS_LABEL })}`
                : ""}
            </Typography>
            {renderLineChart(acumChartData)}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PygAnaliticoYearCompareChart;
